import { createContext, useState, ReactNode, useEffect } from 'react';
import { AuthResponse, LoginCredentials } from '@/types/api';
import { authService } from '@/services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  currentUser: AuthResponse | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

// Create the context with default values
export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isAdmin: false,
  currentUser: null,
  login: async () => { },
  logout: async () => { },
  loading: false,
  error: null
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setUser] = useState<AuthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already authenticated when app loads
  useEffect(() => {
    setLoading(true);
    setError(null);

    const checkAuth = async () => {
      try {
        const response = await authService.checkLogin();
        if (response.data) {
          setIsAuthenticated(true);
          setUser(response.data);
        }
      } catch (err) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginCredentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(credentials, { withCredentials: true });
      if (response) {
        setIsAuthenticated(true);
        setUser(response.data);
      }

    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
      throw new Error(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  const isAdmin = currentUser?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAdmin,
      currentUser,
      login,
      logout,
      loading,
      error
    }}>
      {children}
    </AuthContext.Provider>
  );
};
